import apiClient from './client';
import type {
  ProjectAnnotation,
  AnnotationView,
  ProjectAnnotationContext,
  ProjectAnnotationAcceptancePayload,
  ProjectAnnotationCreatePayload,
  ProjectAnnotationListResponse,
  ProjectAnnotationReply,
  ProjectAnnotationReplyCreatePayload,
  ProjectAnnotationTargetType,
} from '@/types/project-annotations';

export type ProjectAnnotationTargetCategory =
  | 'storyboard'
  | 'storyboard_image'
  | 'storyboard_video'
  | 'resource'
  | 'dialogue'
  | 'voiceover'
  | 'sound_effect';

export interface ProjectAnnotationListParams {
  page?: number;
  pageSize?: number;
  status?: number;
  statuses?: string;
  view?: AnnotationView;
  context?: ProjectAnnotationContext;
  targetType?: ProjectAnnotationTargetType;
  targetCategory?: ProjectAnnotationTargetCategory;
  targetId?: string;
  targetStoryboardId?: string;
  includeClosed?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isProjectAnnotation(value: unknown): value is ProjectAnnotation {
  return isRecord(value) && typeof value.annotationId === 'string';
}

function isProjectAnnotationReply(value: unknown): value is ProjectAnnotationReply {
  return isRecord(value) && typeof value.replyId === 'string';
}

function normalizeProjectAnnotation(annotation: ProjectAnnotation): ProjectAnnotation {
  return {
    ...annotation,
    isActionable: annotation.isActionable ?? false,
    hasUnreadReply: annotation.hasUnreadReply ?? false,
    metadata: isRecord(annotation.metadata) ? annotation.metadata : {},
    replies: Array.isArray(annotation.replies)
      ? annotation.replies.filter(isProjectAnnotationReply)
      : [],
  };
}

function normalizeProjectAnnotationListResponse(
  response: ProjectAnnotationListResponse
): ProjectAnnotationListResponse {
  return {
    ...response,
    items: Array.isArray(response.items)
      ? response.items.filter(isProjectAnnotation).map(normalizeProjectAnnotation)
      : [],
  };
}

export const projectAnnotationApi = {
  async list(
    projectId: string,
    screenplayId: string,
    params?: ProjectAnnotationListParams
  ): Promise<ProjectAnnotationListResponse> {
    const response = (await apiClient.get(
      `/projects/${projectId}/screenplays/${screenplayId}/annotations`,
      {
        params,
      }
    )) as ProjectAnnotationListResponse;
    return normalizeProjectAnnotationListResponse(response);
  },

  async create(
    projectId: string,
    screenplayId: string,
    payload: ProjectAnnotationCreatePayload
  ): Promise<ProjectAnnotation> {
    return (await apiClient.post(
      `/projects/${projectId}/screenplays/${screenplayId}/annotations`,
      payload
    )) as ProjectAnnotation;
  },

  async unreadCount(
    projectId: string,
    screenplayId: string,
    params?: Pick<ProjectAnnotationListParams, 'targetCategory'>
  ): Promise<{ count: number }> {
    return (await apiClient.get(
      `/projects/${projectId}/screenplays/${screenplayId}/annotations/unread-count`,
      { params }
    )) as { count: number };
  },

  async actionableCount(
    projectId: string,
    screenplayId: string,
    params?: Pick<ProjectAnnotationListParams, 'context' | 'targetCategory' | 'targetStoryboardId'>
  ): Promise<{ received: number; sent: number; total: number }> {
    return (await apiClient.get(
      `/projects/${projectId}/screenplays/${screenplayId}/annotations/actionable-count`,
      { params }
    )) as { received: number; sent: number; total: number };
  },

  async reply(
    annotationId: string,
    payload: ProjectAnnotationReplyCreatePayload
  ): Promise<{ replyId: string }> {
    return (await apiClient.post(`/project-annotations/${annotationId}/replies`, payload)) as {
      replyId: string;
    };
  },

  async markRead(annotationId: string): Promise<{ annotationId: string }> {
    return (await apiClient.patch(`/project-annotations/${annotationId}/read`)) as {
      annotationId: string;
    };
  },

  async markReplyRead(annotationId: string): Promise<{ annotationId: string }> {
    return (await apiClient.patch(`/project-annotations/${annotationId}/reply-read`)) as {
      annotationId: string;
    };
  },

  async updateStatus(
    annotationId: string,
    payload: { status: number; note?: string | null }
  ): Promise<ProjectAnnotation> {
    return (await apiClient.patch(
      `/project-annotations/${annotationId}/status`,
      payload
    )) as ProjectAnnotation;
  },

  async updateAcceptance(
    annotationId: string,
    payload: ProjectAnnotationAcceptancePayload
  ): Promise<ProjectAnnotation> {
    return (await apiClient.post(
      `/project-annotations/${annotationId}/acceptance`,
      payload
    )) as ProjectAnnotation;
  },
};
